import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faRoute, faTrash, faCheckCircle, faClock, faLock, faBan, faSearch, faChevronLeft, faChevronRight, faSpinner } from '@fortawesome/free-solid-svg-icons';
import api from '../../Api';
import { useAuth } from '../../contexts/AuthContext';
import SearchableSelect from '../../components/SearchableSelect';

const PER_PAGE = 9;

const FraisDeplacementList = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [surveys, setSurveys] = useState([]);
  const [selectedSurveyId, setSelectedSurveyId] = useState('');
  const [selectedWaveId, setSelectedWaveId] = useState('');
  const [creating, setCreating] = useState(false);

  // Recherche/filtre/page vivent dans l'URL : revenir depuis la fiche d'un projet (bouton
  // Retour ou retour navigateur) rebâtit la liste telle qu'elle était, au lieu de tout perdre.
  const [searchParams, setSearchParams] = useSearchParams();
  const searchTerm = searchParams.get('search') || '';
  // Paramètre absent = 'active' (défaut) ; '' est un choix distinct ("Tous les statuts"),
  // donc on ne peut pas le confondre avec l'absence — d'où le has() plutôt qu'un || .
  const statusFilter = searchParams.has('status') ? searchParams.get('status') : 'active';
  const currentPage = Math.max(1, Number(searchParams.get('page')) || 1);

  const updateParams = (next) => {
    const p = new URLSearchParams(searchParams);
    Object.entries(next).forEach(([key, value]) => {
      const v = typeof value === 'function' ? value(currentPage) : value;
      if (key === 'status') {
        if (v === 'active') p.delete(key); else p.set(key, String(v));
      } else if (v === '' || v === null || v === undefined || (key === 'page' && Number(v) === 1)) {
        p.delete(key);
      } else {
        p.set(key, String(v));
      }
    });
    // replace: pas d'entrée d'historique par frappe, sinon Retour repasse par chaque caractère.
    setSearchParams(p, { replace: true });
  };
  // Un seul updateParams par action : deux appels d'affilée repartiraient tous les deux du
  // même snapshot de searchParams, et le second écraserait le premier.
  const setSearchTerm = (value) => updateParams({ search: value, page: 1 });
  const setStatusFilter = (value) => updateParams({ status: value, page: 1 });
  const setCurrentPage = (value) => updateParams({ page: value });
  const [confirmDialog, setConfirmDialog] = useState(null); // { text, onConfirm }
  const [errorMessage, setErrorMessage] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const res = await api.get('/travel-expenses/projects');
      if (res.data.success) {
        setProjects(res.data.data);
      }
    } catch (err) {
      console.error('Error fetching projects:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSurveys = async () => {
    try {
      // Get F2F surveys from dedicated endpoint (accessible to all travel-expense roles)
      const res = await api.get('/travel-expenses/f2f-surveys');
      if (res.data.success) {
        // Keep surveys that can still receive a project:
        // - non-tracker: no project yet
        // - tracker: at least one vague still free
        const selectable = (res.data.data || []).filter(s => {
          if (s.is_tracker) {
            const waves = s.waves || [];
            return waves.length > 0 && waves.some(w => !w.has_project);
          }
          return !s.has_project;
        });
        setSurveys(selectable);
      }
    } catch (err) {
      console.error('Error fetching surveys:', err);
    }
  };

  const handleCreate = async () => {
    if (!selectedSurveyId) return;
    const sel = surveys.find(s => String(s.id) === String(selectedSurveyId));
    if (sel?.is_tracker && !selectedWaveId) return; // a vague is required for trackers
    try {
      setCreating(true);
      const payload = { survey_id: parseInt(selectedSurveyId) };
      if (selectedWaveId) payload.survey_wave_id = parseInt(selectedWaveId);
      const res = await api.post('/travel-expenses/projects', payload);
      if (res.data.success) {
        setShowCreateModal(false);
        setSelectedSurveyId('');
        setSelectedWaveId('');
        navigate(`/f2f/frais-deplacement/${res.data.data.id}`);
      }
    } catch (err) {
      if (err.response?.data?.existing_id) {
        navigate(`/f2f/frais-deplacement/${err.response.data.existing_id}`);
      }
      console.error('Error creating project:', err);
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = (id) => {
    setConfirmDialog({
      text: 'Supprimer ce projet de frais de déplacement ?',
      onConfirm: async () => {
        setConfirmDialog(null);
        try {
          await api.delete(`/travel-expenses/projects/${id}`);
          fetchProjects();
        } catch (err) {
          setErrorMessage(err.response?.data?.message || 'Erreur lors de la suppression');
        }
      }
    });
  };

  // Format a wave date (Y-m-d or ISO) as dd/mm/yyyy without timezone shifts.
  const fmtWaveDate = (d) => (d ? String(d).split('T')[0].split('-').reverse().join('/') : '?');

  const getStatusBadge = (status) => {
    const map = {
      draft: { label: 'Brouillon', color: '#f59e0b', bg: '#fef3c7', icon: faClock },
      submitted: { label: 'Soumis', color: '#92400e', bg: '#fef3c7', icon: faClock },
      validated: { label: 'Validé', color: '#10b981', bg: '#d1fae5', icon: faCheckCircle },
      rejected: { label: 'Rejeté', color: '#991b1b', bg: '#fee2e2', icon: faBan },
      locked: { label: 'Verrouillé', color: '#6b7280', bg: '#f3f4f6', icon: faLock },
      finished: { label: 'Terminé', color: '#14532d', bg: '#dcfce7', icon: faCheckCircle },
    };
    const s = map[status] || map.draft;
    return (
      <span style={{ background: s.bg, color: s.color, padding: '4px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600 }}>
        <FontAwesomeIcon icon={s.icon} style={{ marginRight: 4 }} />{s.label}
      </span>
    );
  };

  return (
    <div className="fdl-container" style={{ padding: '24px 32px', maxWidth: 1200, margin: '0 auto' }}>
      <style>{`
        @media (max-width: 768px) {
          .fdl-container { padding: 16px !important; }
          .fdl-header { flex-direction: column; align-items: flex-start !important; gap: 12px; }
          .fdl-header h1 { font-size: 18px !important; }
          .fdl-header p { font-size: 12px !important; }
          .fdl-search-row { flex-direction: column !important; }
          .fdl-search-row select { min-width: unset !important; width: 100%; }
          .fdl-grid { grid-template-columns: 1fr !important; }
          .fdl-card-stats { grid-template-columns: 1fr 1fr !important; }
          .fdl-pagination { flex-direction: column; gap: 8px; }
        }
      `}</style>
      <div className="fdl-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#1e293b', margin: 0 }}>
            <FontAwesomeIcon icon={faRoute} style={{ marginRight: 10, color: '#dc2626' }} />
            Frais de Déplacement
          </h1>
          <p style={{ color: '#64748b', fontSize: 14, marginTop: 4 }}>Gestion des frais de déplacement par enquête F2F</p>
        </div>
        {['chef_terrain', 'chef_terrain_superieur'].includes(user?.role) && (
          <button
            onClick={() => { fetchSurveys(); setShowCreateModal(true); }}
            style={{ background: '#dc2626', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', cursor: 'pointer', fontWeight: 600, fontSize: 14, whiteSpace: 'nowrap' }}
          >
            <FontAwesomeIcon icon={faPlus} style={{ marginRight: 6 }} />Nouveau projet
          </button>
        )}
      </div>

      {/* Search & Filter */}
      {!loading && projects.length > 0 && (
        <div className="fdl-search-row" style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <FontAwesomeIcon icon={faSearch} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: 14 }} />
            <input
              type="text"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Rechercher par enquête..."
              style={{ width: '100%', padding: '9px 12px 9px 36px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
            />
          </div>
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            style={{ padding: '9px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, minWidth: 160 }}
          >
            <option value="active">Tous (sauf terminés)</option>
            <option value="">Tous les statuts</option>
            <option value="draft">Brouillon</option>
            <option value="submitted">Soumis</option>
            <option value="validated">Validé</option>
            <option value="rejected">Rejeté</option>
            <option value="locked">Verrouillé</option>
            <option value="finished">Terminé</option>
          </select>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60 }}>
          {/* Même indicateur que /quotas : spinner rouge animé au-dessus du libellé. */}
          <FontAwesomeIcon icon={faSpinner} className="animate-spin" style={{ fontSize: 30, color: '#dc2626', marginBottom: 12 }} />
          <p style={{ color: '#4b5563', margin: 0 }}>Chargement...</p>
        </div>
      ) : projects.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, background: '#f8fafc', borderRadius: 12, border: '2px dashed #e2e8f0' }}>
          <FontAwesomeIcon icon={faRoute} style={{ fontSize: 40, color: '#cbd5e1', marginBottom: 12 }} />
          <p style={{ color: '#64748b', fontSize: 16 }}>Aucun projet de frais de déplacement</p>
          <p style={{ color: '#94a3b8', fontSize: 13 }}>Cliquez sur "Nouveau projet" pour commencer</p>
        </div>
      ) : (() => {
        const filtered = projects.filter(p => {
          if (statusFilter === 'active') {
            if (p.status === 'finished') return false;
          } else if (statusFilter && p.status !== statusFilter) return false;
          if (searchTerm) {
            const term = searchTerm.toLowerCase();
            const title = (p.survey?.title || '').toLowerCase();
            if (!title.includes(term)) return false;
          }
          return true;
        });
        const totalPages = Math.ceil(filtered.length / PER_PAGE);
        const page = Math.min(currentPage, totalPages || 1);
        const paginated = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

        return filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8', fontSize: 14 }}>
            Aucun résultat pour ces filtres
          </div>
        ) : (
        <>
        <div className="fdl-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 16 }}>
          {paginated.map(project => (
            <div
              key={project.id}
              style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: 20, cursor: 'pointer', transition: 'box-shadow 0.2s' }}
              onClick={() => navigate(`/f2f/frais-deplacement/${project.id}`)}
              onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)'}
              onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                <h3 style={{ fontSize: 16, fontWeight: 600, color: '#1e293b', margin: 0, flex: 1 }}>
                  {project.survey?.title || `Enquête #${project.survey_id}`}
                  {project.survey_wave?.label && (
                    <span style={{ marginLeft: 8, padding: '2px 8px', borderRadius: 999, background: '#dbeafe', color: '#1d4ed8', fontSize: 11, fontWeight: 600, verticalAlign: 'middle' }}>
                      {project.survey_wave.label}
                    </span>
                  )}
                </h3>
                {getStatusBadge(project.status)}
              </div>

              <div className="fdl-card-stats" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
                <div style={{ background: '#f8fafc', borderRadius: 8, padding: '8px 12px' }}>
                  <div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase' }}>Prestataires</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#1e293b' }}>{project.supervisor_count || 0}</div>
                </div>
                <div style={{ background: '#f8fafc', borderRadius: 8, padding: '8px 12px' }}>
                  <div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase' }}>Circuits</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#1e293b' }}>{project.circuits_count || 0}</div>
                </div>
                <div style={{ background: '#f8fafc', borderRadius: 8, padding: '8px 12px' }}>
                  <div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase' }}>Budget Total</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: '#1e293b' }}>
                    {(project.total_budget || 0).toLocaleString('fr-FR')} MAD
                  </div>
                </div>
                <div style={{ background: '#f8fafc', borderRadius: 8, padding: '8px 12px' }}>
                  <div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase' }}>Avances payées</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: '#1e293b' }}>
                    {(project.advances_total_paid || 0).toLocaleString('fr-FR')} MAD
                  </div>
                </div>
              </div>

              {['draft', 'rejected'].includes(project.status) && ['chef_terrain', 'chef_terrain_superieur'].includes(user?.role) && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }} onClick={e => e.stopPropagation()}>
                  <button
                    onClick={() => handleDelete(project.id)}
                    style={{ background: '#fef2f2', color: '#ef4444', border: 'none', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 12 }}
                  >
                    <FontAwesomeIcon icon={faTrash} style={{ marginRight: 4 }} />Supprimer
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>

        {totalPages > 1 && (
          <div className="fdl-pagination" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 20, background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', padding: '10px 16px' }}>
            <span style={{ fontSize: 13, color: '#64748b' }}>
              {(page - 1) * PER_PAGE + 1}–{Math.min(page * PER_PAGE, filtered.length)} sur {filtered.length}
            </span>
            <div style={{ display: 'flex', gap: 4 }}>
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={page <= 1}
                style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', cursor: page <= 1 ? 'default' : 'pointer', opacity: page <= 1 ? 0.4 : 1, fontSize: 13 }}
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                .reduce((acc, p, idx, arr) => {
                  if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
                  acc.push(p);
                  return acc;
                }, [])
                .map((p, idx) =>
                  p === '...' ? (
                    <span key={`dot-${idx}`} style={{ padding: '6px 4px', fontSize: 13, color: '#94a3b8' }}>...</span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => setCurrentPage(p)}
                      style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid ' + (p === page ? '#dc2626' : '#d1d5db'), background: p === page ? '#dc2626' : '#fff', color: p === page ? '#fff' : '#374151', cursor: 'pointer', fontWeight: p === page ? 600 : 400, fontSize: 13 }}
                    >
                      {p}
                    </button>
                  )
                )}
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', cursor: page >= totalPages ? 'default' : 'pointer', opacity: page >= totalPages ? 0.4 : 1, fontSize: 13 }}
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </div>
          </div>
        )}
        </>
        );
      })()}

      {/* Create Modal */}
      {showCreateModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 28, width: 440, maxWidth: '90%' }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 18, fontWeight: 700 }}>Nouveau projet de frais de déplacement</h3>
            <label style={{ fontSize: 13, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 6 }}>Enquête F2F</label>
            <div style={{ marginBottom: 20 }}>
              <SearchableSelect
                options={surveys}
                selectedValue={selectedSurveyId ? parseInt(selectedSurveyId) : null}
                onChange={(value) => { setSelectedSurveyId(value ? String(value) : ''); setSelectedWaveId(''); }}
                placeholder="-- Sélectionner une enquête --"
                searchPlaceholder="Rechercher par titre..."
                getOptionLabel={(s) => s.title}
                getOptionMeta={(s) => s.client?.name || null}
                maxHeight="220px"
              />
            </div>

            {/* Vague picker — tracker surveys only */}
            {(() => {
              const sel = surveys.find(s => String(s.id) === String(selectedSurveyId));
              if (!sel?.is_tracker) return null;
              const waves = sel.waves || [];
              return (
                <div style={{ marginBottom: 20 }}>
                  <label style={{ fontSize: 13, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 6 }}>Vague</label>
                  {waves.length === 0 ? (
                    <div style={{ fontSize: 13, color: '#94a3b8' }}>Aucune vague définie pour cette enquête.</div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {waves.map(w => {
                        const isSel = String(selectedWaveId) === String(w.id);
                        const disabled = w.has_project;
                        return (
                          <div
                            key={w.id}
                            onClick={() => { if (!disabled) setSelectedWaveId(String(w.id)); }}
                            style={{
                              padding: '8px 12px', borderRadius: 8,
                              border: `1px solid ${isSel ? '#dc2626' : '#d1d5db'}`,
                              background: disabled ? '#f3f4f6' : (isSel ? '#fef2f2' : '#fff'),
                              cursor: disabled ? 'not-allowed' : 'pointer',
                              opacity: disabled ? 0.6 : 1, fontSize: 14
                            }}
                          >
                            <div style={{ fontWeight: 600, color: '#1e293b' }}>{w.label || 'Vague'}</div>
                            <div style={{ fontSize: 12, color: '#94a3b8' }}>
                              {fmtWaveDate(w.date_debut)} → {fmtWaveDate(w.date_fin)}{disabled ? ' · Projet existant' : ''}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })()}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button
                onClick={() => { setShowCreateModal(false); setSelectedSurveyId(''); setSelectedWaveId(''); }}
                style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', fontSize: 14 }}
              >
                Annuler
              </button>
              {(() => {
                const sel = surveys.find(s => String(s.id) === String(selectedSurveyId));
                const blocked = !selectedSurveyId || creating || (sel?.is_tracker && !selectedWaveId);
                return (
                  <button
                    onClick={handleCreate}
                    disabled={blocked}
                    style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: '#dc2626', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: 14, opacity: blocked ? 0.5 : 1 }}
                  >
                    {creating ? 'Création...' : 'Créer'}
                  </button>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* CONFIRM DIALOG */}
      {confirmDialog && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: 420, maxWidth: '90%' }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 17, fontWeight: 700 }}>Confirmation</h3>
            <p style={{ fontSize: 14, color: '#475569', marginBottom: 20 }}>{confirmDialog.text}</p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button onClick={() => setConfirmDialog(null)} style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', fontSize: 14 }}>Annuler</button>
              <button onClick={confirmDialog.onConfirm} style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: '#dc2626', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>Confirmer</button>
            </div>
          </div>
        </div>
      )}

      {/* ERROR MESSAGE MODAL */}
      {errorMessage && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: 420, maxWidth: '90%' }}>
            <h3 style={{ margin: '0 0 12px', fontSize: 17, fontWeight: 700, color: '#dc2626' }}>Erreur</h3>
            <p style={{ fontSize: 14, color: '#475569', marginBottom: 20 }}>{errorMessage}</p>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={() => setErrorMessage(null)} style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: '#dc2626', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>Fermer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FraisDeplacementList;
